import './BuyersFeesTable.scss';
import React from 'react';
import { FormGroup, Col, Row } from 'reactstrap';
import { Input } from '@availity/form';
import InfoTooltip from '../../../../../components/info-tooltip/info-tooltip';
import { MarketplaceCurrencyField } from '../MarketplaceSettingsFields/MarketplaceCurrencyField';
import {
    FeeRefundStatus,
    FeeRefundStatuses,
} from '../../../../../slices/marketplace/pricing/models/fee-refund-status.enum';
import { BuyersFees } from '../../../../../slices/marketplace/pricing/models/buyers-fees-model';
import { MarketplacePricing } from '../../../../../slices/marketplace/pricing/models/marketplace-pricing.model';

const NoFeeText = 'No fee';

interface BuyersFeesTableProps {
    brokerageType:
        | 'discountBrokeragePricing'
        | 'fullServiceBrokeragePricing'
        | 'sfasPricing';
    buyersFees: BuyersFees;
    onChange: (newState: BuyersFees) => void;
    setFormFieldValue: (name: string, value: string) => void;
    formState: MarketplacePricing;
    isViewMode?: boolean;
    isReadonly?: boolean;
}

const BuyersFeesTable = (props: BuyersFeesTableProps) => {
    const labelFieldWidthMd = 2;
    const multiFieldWidthMd = 5;
    const refundableFieldWidthMd = 2;
    const creditableFieldWidthMd = 2;

    const partiallyRefundableTooltipText =
        'The application fee will be partially refunded in each of the following circumstances: ' +
        '1. 50% refund if a completed process yeilds no LOIs, ' +
        '2. 25% refund if a completed process yeilds ONLY 1 LOI, ' +
        "3. Up to 50% refund, at VetValue's discretion, depending upon the circumstances, if the engagement application is denied by VetValue";

    const formatCurrency = (value: number) => {
        if (value == null) return '$0';
        const isWholeNumber = value % 1 === 0;
        return '$' + (isWholeNumber ? value.toString() : value.toFixed(2));
    };

    const isNoFee = (fieldId: keyof BuyersFees) =>
        props.isViewMode &&
        props.brokerageType !== 'sfasPricing' &&
        props.buyersFees[fieldId] === 0;

    const priceField = (fieldId: keyof BuyersFees) => (
        <>
            <div className="shorter-field">
                {props.isViewMode ? (
                    <div className="view-mode-value">
                        {isNoFee(fieldId)
                            ? NoFeeText
                            : formatCurrency(props.buyersFees[fieldId] as number)}
                    </div>
                ) : (
                    <MarketplaceCurrencyField
                        name={`${props.brokerageType}.buyersFees.${fieldId}`}
                        value={
                            props.formState[props.brokerageType].buyersFees[
                                fieldId
                            ] as number
                        }
                        setFormValue={(name: string, value: string): void => {
                            props.setFormFieldValue(name, value);
                            const newState = {
                                ...props.buyersFees,
                            };
                            const numVal = Number(value);
                            (newState[fieldId] as number) = numVal;
                            props.onChange({
                                ...newState,
                            });
                        }}
                        isReadonly={props.isReadonly}
                    />
                )}
            </div>
        </>
    );

    const refundableField = (fieldId: keyof BuyersFees, isHidden: boolean) => {
        const getRefundStatusText = (status: number) => {
            const statusItem = FeeRefundStatuses.find(
                (item) => item.id === status
            );
            return statusItem ? statusItem.name : 'No';
        };

        if (isHidden) {
            return null;
        }

        return (
            <>
                <div
                    className={`shorter-field ${
                        props.buyersFees[fieldId] == FeeRefundStatus.Partially
                            ? ''
                            : 'info-tooltip-offset'
                    }`}>
                    {props.isViewMode ? (
                        <div className="view-mode-value">
                            {getRefundStatusText(
                                props.buyersFees[fieldId] as number
                            )}
                        </div>
                    ) : (
                        <Input
                            type="select"
                            name={`buyersFees.${fieldId}`}
                            value={
                                props.formState[props.brokerageType].buyersFees[
                                    fieldId
                                ] as number
                            }
                            onChange={(event) => {
                                const numVal = Number(
                                    event.currentTarget.value
                                );
                                const newState = {
                                    ...props.buyersFees,
                                };
                                (newState[fieldId] as number) = numVal;
                                const formFieldName = `${props.brokerageType}.buyersFees.${fieldId}`;
                                props.setFormFieldValue(
                                    formFieldName,
                                    numVal.toString()
                                );
                                props.onChange({ ...newState });
                            }}
                            disabled={props.isReadonly}>
                            {FeeRefundStatuses.map((item) => (
                                <option value={item.id} key={fieldId + item.id}>
                                    {item.name}
                                </option>
                            ))}{' '}
                        </Input>
                    )}
                </div>
                {props.buyersFees[fieldId] == FeeRefundStatus.Partially ? (
                    <InfoTooltip
                        idText={`buyersFees.${fieldId}`}
                        text={partiallyRefundableTooltipText}
                    />
                ) : (
                    []
                )}
            </>
        );
    };

    return (
        <div className="buyers-fees-table">
            <FormGroup>
                <Row className="center-items fee-item-row margin-bottom-1-rem buyers-fees-column-names d-none d-md-flex">
                    <Col md={labelFieldWidthMd}>
                        <label>Fee Type</label>
                    </Col>
                    <Col
                        md={multiFieldWidthMd}
                        className="conditional-text-center">
                        <label>Fee Amount</label>
                    </Col>
                    <Col
                        md={refundableFieldWidthMd}
                        className="conditional-text-center aligned-md-2">
                        <label className="refundable-label">Refundable</label>
                    </Col>
                </Row>

                <Row className="center-items fee-item-row margin-bottom-1-rem">
                    <Col md={labelFieldWidthMd}>
                        <div className="label-with-tooltip">
                            <label>Seller Identification Fee</label>
                        </div>
                    </Col>
                    <Col
                        md={multiFieldWidthMd}
                        className="right-padding-5 buyers-fees-amount-cell">
                        <label className="d-md-none buyers-fees-cell-label">
                            Fee Amount
                        </label>
                        <div className="multi-field">
                            {priceField('sellerIdFeePerDvm')}
                            {!isNoFee('sellerIdFeePerDvm') && (
                                <label className="label-right">
                                    * FTE DVMs
                                </label>
                            )}
                        </div>
                    </Col>
                    <Col
                        md={refundableFieldWidthMd}
                        className="conditional-text-center buyers-fees-refundable-cell aligned-md-2">
                        {!isNoFee('sellerIdFeePerDvm') && (
                            <label className="d-md-none buyers-fees-cell-label">
                                Refundable
                            </label>
                        )}
                        <div className="multi-field flex-hor-center">
                            {refundableField(
                                'sellerIdFeeRefundable',
                                isNoFee('sellerIdFeePerDvm')
                            )}
                        </div>
                    </Col>
                </Row>

                <Row className="center-items fee-item-row margin-bottom-1-rem">
                    <Col md={labelFieldWidthMd}>
                        <div className="label-with-tooltip">
                            <label>PIP/PIQ Purchase Fee</label>
                        </div>
                    </Col>
                    <Col
                        md={multiFieldWidthMd}
                        className="right-padding-5 buyers-fees-amount-cell">
                        <label className="d-md-none buyers-fees-cell-label">
                            Fee Amount
                        </label>
                        <div className="multi-field">
                            {priceField('pipAccessFeePerDvm')}
                            {!isNoFee('pipAccessFeePerDvm') && (
                                <label className="label-right">
                                    * FTE DVMs
                                </label>
                            )}
                        </div>
                    </Col>
                    <Col
                        md={refundableFieldWidthMd}
                        className="conditional-text-center buyers-fees-refundable-cell aligned-md-2">
                        {!isNoFee('pipAccessFeePerDvm') && (
                            <label className="d-md-none buyers-fees-cell-label">
                                Refundable
                            </label>
                        )}
                        <div className="multi-field flex-hor-center">
                            {refundableField(
                                'pipAccessFeeRefundable',
                                isNoFee('pipAccessFeePerDvm')
                            )}
                        </div>
                    </Col>
                </Row>

                <Row className="center-items fee-item-row margin-bottom-1-rem">
                    <Col md={labelFieldWidthMd}>
                        <div className="label-with-tooltip">
                            <label>DFM Purchase Fee</label>
                        </div>
                    </Col>
                    <Col
                        md={multiFieldWidthMd}
                        className="right-padding-5 buyers-fees-amount-cell">
                        <label className="d-md-none buyers-fees-cell-label">
                            Fee Amount
                        </label>
                        <div className="multi-field">
                            {priceField('dfmAccessFeePerDvm')}
                            {!isNoFee('dfmAccessFeePerDvm') && (
                                <label className="label-right">
                                    * FTE DVMs
                                </label>
                            )}
                        </div>
                    </Col>
                    <Col
                        md={refundableFieldWidthMd}
                        className="conditional-text-center buyers-fees-refundable-cell aligned-md-2">
                        {!isNoFee('dfmAccessFeePerDvm') && (
                            <label className="d-md-none buyers-fees-cell-label">
                                Refundable
                            </label>
                        )}
                        <div className="multi-field flex-hor-center">
                            {refundableField(
                                'dfmAccessFeeRefundable',
                                isNoFee('dfmAccessFeePerDvm')
                            )}
                        </div>
                    </Col>
                </Row>

                <Row className="center-items fee-item-row margin-bottom-1-rem">
                    <Col md={labelFieldWidthMd}>
                        <div className="label-with-tooltip">
                            <label>Seller Meeting Fee</label>
                            {props.isViewMode ? (
                                []
                            ) : (
                                <InfoTooltip text="50% of this fee is transferred to the seller in form of a credit against their transaction fee, encouraging sellers to meet the buyer" />
                            )}
                        </div>
                    </Col>

                    <Col md={multiFieldWidthMd} className="right-padding-5">
                        <label className="d-md-none buyers-fees-cell-label">
                            Fee Amount
                        </label>
                        <Col className="multi-field left-padding-0 margin-bottom-half-rem">
                            {priceField('sellerMeetingFeeVirtual')}
                            <label className="label-right">
                                {isNoFee('sellerMeetingFeeVirtual')
                                    ? 'for virtual meetings'
                                    : 'per virtual meeting'}
                            </label>
                        </Col>

                        <Col className="multi-field left-padding-0">
                            {priceField('sellerMeetingFeeInPerson')}
                            <label className="label-right">
                                {isNoFee('sellerMeetingFeeInPerson')
                                    ? 'for in-person meetings'
                                    : 'per in-person meeting'}
                            </label>
                        </Col>
                    </Col>

                    <Col
                        md={refundableFieldWidthMd}
                        className="conditional-text-center buyers-fees-refundable-cell aligned-md-2">
                        {!(
                            isNoFee('sellerMeetingFeeVirtual') &&
                            isNoFee('sellerMeetingFeeInPerson')
                        ) && (
                            <label className="d-md-none buyers-fees-cell-label">
                                Refundable
                            </label>
                        )}
                        <div className="multi-field flex-hor-center">
                            {refundableField(
                                'sellerMeetingFeeRefundable',
                                isNoFee('sellerMeetingFeeVirtual') &&
                                    isNoFee('sellerMeetingFeeInPerson')
                            )}
                        </div>
                    </Col>
                </Row>
            </FormGroup>
        </div>
    );
};

export default BuyersFeesTable;
